import { Col, ExtraInfo, Loading, Row, Table } from '@elotech/components';
import { AxiosPromise } from 'axios';
import {
  IFrame,
  IFrameContainer,
  InlineButton,
  TableContainer
} from 'common/components';
import { Arquivo } from 'common/type';
import { getContentType, getExtension } from 'common/utils';
import React, { useCallback, useEffect, useState } from 'react';
import { Image } from 'react-bootstrap';

import styles from './documentViewer.module.css';

type Props = {
  documents: Arquivo[];
  onDownload: (arquivo: Arquivo) => AxiosPromise;
  parentIsLoading: boolean;
  getDocumentCaption?: (document: Arquivo) => React.ReactNode;
};

export const DocumentViewer: React.FC<Props> = ({
  documents,
  onDownload,
  parentIsLoading,
  getDocumentCaption
}) => {
  const [showingFile, setShowingFile] = useState<Arquivo>();
  const [showingLink, setShowingLink] = useState<string>();
  const [loading, setLoading] = useState<boolean>(false);
  const VIEW_TYPES = [
    'PDF',
    'PNG',
    'JPG',
    'JPEG',
    'GIF',
    'BMP',
    'SVG',
    'MP4',
    'WEBM',
    'OGV',
    'OGG'
  ];

  const IMAGE_TYPES = ['PNG', 'JPG', 'JPEG', 'GIF', 'BMP', 'SVG'];

  const doDownloadFile = useCallback(() => {
    const link = document.createElement('a');
    link.href = showingLink!;
    link.download = showingFile!.nome!;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [showingLink, showingFile]);

  useEffect(() => {
    if (!showingFile) {
      return;
    }
    let fileURL: string;
    let canceled = false;

    setShowingLink(undefined);
    setLoading(true);
    onDownload(showingFile!)
      .then(({ data, headers }) => {
        if (canceled) {
          return;
        }

        const contentDisposition = headers?.['Content-Disposition'];
        const responseFileName = contentDisposition?.substring(
          contentDisposition.indexOf('"') + 1,
          contentDisposition.lastIndexOf('"')
        );

        const type = getContentType(responseFileName || showingFile!.nome);
        const blob = new Blob([data], { type });

        fileURL = URL.createObjectURL(blob);

        setShowingLink(fileURL);
      })
      .finally(() => {
        if (!canceled) {
          setLoading(false);
        }
      });

    return () => {
      canceled = true;
      URL.revokeObjectURL(fileURL);
    };
  }, [showingFile]);

  const onNext = (forward: boolean) => {
    const canViewList = documents.filter(doc =>
      VIEW_TYPES.includes(getExtension(doc.nome))
    );

    if (!canViewList.length) {
      return;
    }

    if (canViewList.length === 1 && showingFile === canViewList[0]) {
      return;
    }

    if (!showingFile) {
      return setShowingFile(canViewList[forward ? 0 : canViewList.length - 1]);
    }

    let nextIndex =
      canViewList.findIndex(doc => doc === showingFile) + (forward ? 1 : -1);

    if (nextIndex >= canViewList.length) {
      nextIndex = 0;
    }

    if (nextIndex < 0) {
      nextIndex = canViewList.length - 1;
    }

    return setShowingFile(canViewList[nextIndex]);
  };

  const isImage = () => {
    return showingLink && IMAGE_TYPES.includes(getExtension(showingFile?.nome));
  };

  return (
    <>
      <Row>
        <Col sm={3} md={3}>
          <TableContainer>
            <Table<Arquivo>
              values={documents}
              highlight={doc => doc === showingFile}
              onClickRow={setShowingFile}
              keyExtractor={(doc, index) => `${index} - ${doc.nome}`}
              loading={parentIsLoading}
            >
              <Table.Column
                style={{ cursor: 'pointer' }}
                header="Ordem"
                value={(document: any) => document?.ordemCalculada}
              />
              <Table.Column<Arquivo>
                style={{ cursor: 'pointer' }}
                header="Nome Documento"
                value={doc => {
                  const caption = getDocumentCaption?.(doc);

                  return (
                    <div className={styles.documentName}>
                      <div>
                        {!VIEW_TYPES.includes(getExtension(doc?.nome)) && (
                          <ExtraInfo
                            letter={'D'}
                            color={'negative'}
                            value={'Apenas Download'}
                          />
                        )}
                        {doc.nome}
                      </div>
                      {caption && (
                        <small className={styles.documentCaption}>
                          {caption}
                        </small>
                      )}
                    </div>
                  );
                }}
              />
            </Table>
          </TableContainer>
        </Col>
        <Col sm={9} md={9}>
          <Row>
            <div className="container" style={{ marginTop: '20px' }}>
              <Col sm={5} md={5}>
                <InlineButton
                  style={{ width: '100%' }}
                  label="Anterior"
                  icon="fa fa-arrow-left"
                  onClick={() => {
                    onNext(false);
                  }}
                  type="button"
                />
              </Col>
              <Col sm={2} md={2}>
                <InlineButton
                  style={{ width: '100%' }}
                  label="Baixar"
                  icon="fa fa-download"
                  disabled={!showingFile || !showingLink}
                  onClick={doDownloadFile}
                  type="button"
                />
              </Col>
              <Col sm={5} md={5}>
                <InlineButton
                  style={{ width: '100%' }}
                  label="Próximo"
                  icon="fa fa-arrow-right"
                  className="icon-right"
                  onClick={() => {
                    onNext(true);
                  }}
                  type="button"
                />
              </Col>
            </div>
          </Row>
          <Row style={{ position: 'relative' }}>
            <Loading loading={loading} contain={true} />
            {isImage() ? (
              <Col
                md={12}
                className="mt-xs ml-xs"
                style={{ display: 'flex', justifyContent: 'center' }}
              >
                <Image src={showingLink} responsive />
              </Col>
            ) : (
              <IFrameContainer>
                <IFrame path={showingLink || 'about:blank'} />
              </IFrameContainer>
            )}
          </Row>
        </Col>
      </Row>
    </>
  );
};
